/**
 * The order number, large, and a countdown home.
 *
 * The receipt prints from local data — it does not need the server, which is
 * what lets an offline card sale still hand the customer a piece of paper.
 *
 * **Two receipts, and which one prints is decided by whether the server
 * answered.** Online, the sale is filed with e-Barimt before this screen is
 * reached, so the slip is a tax receipt with a lottery number and a QR on it.
 * Offline, none of that is true and neither is the rest: the server has never
 * heard of the order, so there is no sequence number, nothing on the attendant
 * screen, and nobody in the shop who can look the sale up. What the customer
 * gets then is a **payment receipt** — the drinks, the amount, and the
 * terminal's approval code — and a line telling them to take it to the counter,
 * because a person is now the only thing that can turn it into a coffee.
 *
 * There is no poll. The filing is synchronous on the capture path
 * (`services/fiscal.ts:fileNow`), so what `orders.get` answers here is final:
 * a screen that kept asking every three seconds was a screen hoping the queue
 * would beat a twelve-second timer, and telling the customer nothing they could
 * act on when it did not.
 *
 * **One state asks the customer something.** When e-Barimt is down 1C posts
 * the sale anyway and holds the receipt (`deferred`), and when it answers
 * `need_phone` the view says `phoneWanted`. A sheet then asks for a phone
 * number, which goes to 1C through `orders.ebarimtPhone` so it can deliver the
 * receipt later. The countdown home stops while it is up — a customer typing a
 * number is not a customer who walked away — and skipping it costs that one
 * receipt its recipient and nothing else.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { lineLabel } from '@mgc/domain'
import { orderNumberOf } from '@mgc/receipt'
import type { ReceiptTemplate, SlipOrder } from '@mgc/receipt'
import { Icon } from '@/components/icons'
import { Button, Notice, Sheet, Spinner } from '@/components/ui'
import { NumberPad } from '@/components/keyboard'
import { useRefusal, useT } from '@/i18n'
import { refusalOf, trpc } from '@/api/client'
import { printReceipt } from '@/shell/print'
import { templateInHand } from '@/services/receipt-template'
import { useKiosk, usePricedCart } from '@/store/kiosk'
import { useSession } from '@/store/session'
import { totalsOf } from '@mgc/domain'

/** How long the customer looks at their number before the kiosk resets. */
const HOME_AFTER_MS = 12_000

/** A Mongolian number, without the country code. `zPhone` is the same rule. */
const PHONE_DIGITS = 8

/**
 * The held arrangement with one label rewritten.
 *
 * A whole section kind for «take this to the counter» would be a protocol
 * change, a console control and a migration for a sentence that appears in one
 * state. The fiscal block already prints a line when there is no lottery
 * number; offline, this is what that line should say.
 */
const takeToCounter = (sentence: string): ReceiptTemplate => {
  const held = templateInHand()
  return { ...held, labels: { ...held.labels, fiscalPending: sentence } }
}

const EMOJIS = ['🎉', '🔥', '✨', '💪', '🚀', '⭐', '💯', '🎯', '👏', '🙌', '🌟', '🏆', '💎', '🎊', '☕️']

export function Success() {
  const t = useT()
  const orderId = useKiosk((s) => s.orderId)
  const reset = useKiosk((s) => s.reset)
  const branch = useSession((s) => s.branch)
  const { lines } = usePricedCart()

  const capture = useKiosk((s) => s.capture)

  const [displayNumber, setDisplayNumber] = useState<string | null>(null)
  const [printProblem, setPrintProblem] = useState(false)
  const [fiscal, setFiscal] = useState<{ state: string; lottery: string | null; phoneSent: boolean } | null>(null)
  /** The phone sheet is up. The countdown home waits for it. */
  const [askingPhone, setAskingPhone] = useState(false)
  /** The server did not answer. Everything below reads differently when it is true. */
  const [unconfirmed, setUnconfirmed] = useState(false)
  const printed = useRef(false)

  // A kiosk that feels mechanical feels broken. It costs nothing.
  const emoji = useMemo(() => EMOJIS[Math.floor(Math.random() * EMOJIS.length)]!, [])

  useEffect(() => {
    if (askingPhone) return
    const timer = setTimeout(reset, HOME_AFTER_MS)
    return () => clearTimeout(timer)
  }, [reset, askingPhone])

  useEffect(() => {
    if (orderId === null || printed.current) return
    printed.current = true

    const run = async () => {
      const totals = totalsOf({ lines, payments: [] })

      // Ask the server for the authoritative view — it has the sequence number
      // and the fiscal state. If it cannot answer, print from what is on hand:
      // a receipt is not worth withholding because the network is down.
      let view: Awaited<ReturnType<typeof trpc.orders.get.query>> | null = null
      try {
        view = await trpc.orders.get.query({ id: orderId })
        setDisplayNumber(orderNumberOf(view))
        setFiscal(
          view.fiscal === null
            ? null
            : { state: view.fiscal.state, lottery: view.fiscal.lottery, phoneSent: view.fiscal.phoneSent }
        )
        if (view.fiscal?.phoneWanted === true && !view.fiscal.phoneSent) setAskingPhone(true)
      } catch {
        // The server has never heard of this order, so nothing in the shop has
        // either — there is no number to call and no list to find it on.
        setDisplayNumber(null)
        setUnconfirmed(true)
      }

      const slip: SlipOrder = {
        displayNumber: view?.displayNumber ?? '···',
        orderType: useKiosk.getState().cart.orderType ?? 'eat_in',
        placedAt: view?.placedAt ?? new Date().toISOString(),
        gross: view?.gross ?? totals.gross,
        net: view?.net ?? totals.net,
        vat: view?.vat ?? totals.vat,
        savings: view?.savings ?? totals.savings,
        change: view?.change ?? totals.change,
        lines:
          view?.lines.map((l) => ({
            name: l.name,
            quantity: l.quantity,
            total: l.total,
            note: l.note,
            addOns: l.addOns
          })) ??
          lines.map((l) => ({
            name: lineLabel(l),
            quantity: l.selection.quantity,
            total: l.total,
            note: l.selection.note ?? null,
            addOns: l.addOns.map((a) => ({ label: a.addOn.label, quantity: a.quantity, total: a.total }))
          })),
        /*
          The kiosk's own copy when the server has none. This was
          `view?.payments ?? []`, which printed no payment line at all on the one
          slip that has to prove the money moved — the customer is being sent to
          a counter with it, and «paid by card, ₮6,900, approval A1» is what
          somebody there has to read.
        */
        /*
          Captured only. The view lists every payment row on the order, and a
          QR the customer cancelled is one — so pick QPay, cancel, pick it
          again printed «Төлбөрийн дүн 20» over a ₮10 sale, counting money that
          never moved.
        */
        payments:
          view?.payments
            .filter((p) => p.state === 'captured')
            .map((p) => ({ method: p.method, amount: p.amount, reference: p.reference })) ??
          (capture === null ? [] : [{ method: capture.method, amount: capture.amount, reference: capture.reference }]),
        company: view?.company ?? null,
        fiscal: view?.fiscal ?? null
      }

      const outcome = await printReceipt(
        slip,
        {
          name: branch?.name ?? 'MegaCoffee',
          address: branch?.address ?? null,
          wifiName: branch?.wifiName ?? null,
          wifiPassword: branch?.wifiPassword ?? null
        },
        // An unconfirmed sale's fiscal line is not «being filed» — nothing is
        // filing it, and nothing will until this kiosk reaches the server again.
        // What the line has to say instead is what the customer should now do.
        view === null ? takeToCounter(t('success.takeToCounter')) : templateInHand()
      )

      if (outcome.via === 'browser' && outcome.because !== 'no-shell') setPrintProblem(true)
    }

    void run()
  }, [orderId, lines, branch, capture, t])

  return (
    <>
      {/*
      Yellow, like the attract screen and unlike everything between them: the
      order is over, and the customer should be able to tell that from across
      the shop while they are walking away from the panel.
    */}
      <button
        type="button"
        onClick={reset}
        className="animate-fade-in flex h-full w-full flex-col items-center overflow-y-auto bg-[var(--color-brand)] p-10 text-center"
      >
        {/*
        The number in the upper band, where it is read from wherever the
        customer ends up standing while they wait, and the way out at the foot
        where a hand is. This screen used to centre the lot, which put the one
        thing they have to carry away halfway down the glass and the "done"
        button below the reach line.
      */}
        <div className="flex flex-1 flex-col items-center justify-center gap-10 pt-6">
          <div className="grid size-40 shrink-0 place-items-center rounded-[var(--radius-tile)] bg-[var(--color-dark)] shadow-[var(--shadow-tile)]">
            <Icon name="check" className="size-24 text-[var(--color-brand)]" />
          </div>

          <h1 className="display flex items-center gap-5 text-[length:var(--text-kiosk-xl)] text-[var(--color-brand-ink)] uppercase">
            <span aria-hidden="true">{emoji}</span>
            {t('success.title')}
          </h1>

          {/*
          The number, on white, and nothing else on the card with it. It is the
          one thing the customer has to carry away from this screen, and the size
          is what makes it readable from wherever they end up standing while they
          wait.
        */}
          {/*
          An unconfirmed sale has no number, and drawing the eyebrow over a
          spinner that will never resolve tells the customer to wait for
          something nobody is going to call. The card says what to do instead,
          and the paper in their hand says the same thing.
        */}
          <div className="w-full max-w-3xl rounded-[var(--radius-sheet)] bg-[var(--color-paper-raised)] p-14 shadow-[var(--shadow-tile)]">
            {unconfirmed ? (
              <>
                <p className="eyebrow">{t('success.paid')}</p>
                <p className="display-upright mt-5 text-[length:var(--text-kiosk-lg)]">{t('success.takeToCounter')}</p>
              </>
            ) : (
              <>
                <p className="eyebrow">{t('success.number')}</p>
                {displayNumber === null ? (
                  <div className="grid h-52 place-items-center">
                    <Spinner />
                  </div>
                ) : (
                  <p className="display mt-5 text-[length:var(--text-kiosk-3xl)] tabular-nums">{displayNumber}</p>
                )}

                <p className="display-upright mt-12 border-t-4 border-dotted border-[var(--color-ink-ghost)] pt-12 text-[length:var(--text-kiosk-lg)] text-[var(--color-ink-faint)]">
                  {t('success.wait')}
                </p>
              </>
            )}
          </div>

          {(printProblem || fiscal !== null || unconfirmed) && (
            <div className="flex w-full max-w-3xl flex-col gap-4">
              {printProblem && <Notice tone="warn">{t('success.printFailed')}</Notice>}

              {/* The one case where the printer mattering more than usual is worth
                saying twice: no paper *and* no record in the shop is a sale
                only the attendant screen's outbox banner can recover. */}
              {unconfirmed && <Notice tone="warn">{t('success.unconfirmed')}</Notice>}

              {/* `skipped` is not a warning: nothing went wrong and nothing is
                coming. It is a test sale, which files no e-Barimt because 1C
                has never heard of the SKU — so it reads as a plain statement
                rather than as a filing somebody should wait for. */}
              {fiscal !== null && (
                <Notice
                  tone={
                    fiscal.state === 'filed' || fiscal.phoneSent
                      ? 'good'
                      : fiscal.state === 'skipped'
                        ? 'plain'
                        : 'warn'
                  }
                >
                  {fiscal.state === 'filed'
                    ? `${t('success.fiscalFiled')}${fiscal.lottery === null ? '' : ` · ${t('success.lottery')} ${fiscal.lottery}`}`
                    : fiscal.state === 'skipped'
                      ? t('success.fiscalSkipped')
                      : fiscal.state === 'deferred'
                        ? fiscal.phoneSent
                          ? t('success.phoneSent')
                          : t('success.fiscalDeferred')
                        : t('success.fiscalPending')}
                </Notice>
              )}
            </div>
          )}
        </div>

        <span className="btn-dark tap mt-10 shrink-0 px-16 text-[length:var(--text-kiosk-lg)] uppercase">
          {t('success.done')}
        </span>
      </button>

      {/*
      A sibling of the button, not a child. The sheet portals to the body, but
      React bubbles a portal's events through the component tree — inside the
      button, every key on the pad would also have been a tap on «done».
    */}
      {orderId !== null && (
        <EbarimtPhone
          open={askingPhone}
          orderId={orderId}
          onSent={() => {
            setFiscal((current) => (current === null ? current : { ...current, phoneSent: true }))
            setAskingPhone(false)
          }}
          onSkip={() => setAskingPhone(false)}
        />
      )}
    </>
  )
}

/**
 * The customer's phone number, for an e-Barimt 1C will send later.
 *
 * The same keypad the company receipt uses, for the same reason: the panel has
 * no keyboard and Windows' touch keyboard does not reliably appear under the
 * kiosk lock.
 */
function EbarimtPhone({
  open,
  orderId,
  onSent,
  onSkip
}: {
  open: boolean
  orderId: string
  onSent: () => void
  onSkip: () => void
}) {
  const t = useT()
  const refusalText = useRefusal()
  const [phone, setPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const send = async () => {
    setBusy(true)
    setProblem(null)
    try {
      await trpc.orders.ebarimtPhone.mutate({ orderId, phone })
      onSent()
    } catch (error) {
      setProblem(refusalText(refusalOf(error)))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet open={open} onClose={onSkip} labelledBy="ebarimt-phone-title">
      <div className="flex flex-col gap-7 p-10">
        <h2 id="ebarimt-phone-title" className="display text-[length:var(--text-kiosk-lg)] uppercase">
          {t('success.phoneTitle')}
        </h2>
        <p className="text-[length:var(--text-kiosk)] text-[var(--color-ink-soft)]">{t('success.phoneHint')}</p>

        <p
          aria-live="polite"
          className="display rounded-[var(--radius-card)] border-2 border-[var(--color-line)] bg-[var(--color-paper-sunk)] px-8 py-6 text-[length:var(--text-kiosk-xl)] tabular-nums"
        >
          {phone.length === 0 ? <span className="text-[var(--color-ink-ghost)]">—</span> : phone}
        </p>

        <NumberPad
          onKey={(digit) => setPhone((current) => (current + digit).slice(0, PHONE_DIGITS))}
          onBackspace={() => setPhone((current) => current.slice(0, -1))}
          onClear={() => setPhone('')}
        />

        {problem !== null && <Notice tone="bad">{problem}</Notice>}

        <div className="flex items-center gap-5">
          <Button variant="text" className="text-[var(--color-ink-faint)]" onClick={onSkip}>
            {t('success.phoneSkip')}
          </Button>
          <Button
            size="large"
            className="ml-auto uppercase"
            disabled={phone.length !== PHONE_DIGITS || busy}
            onClick={() => void send()}
          >
            {t('success.phoneSend')}
          </Button>
        </div>
      </div>
    </Sheet>
  )
}
